import {AnyAction} from 'redux';

import {AppDispatch, StoreState, Thunk} from '~/frontend/src/store';
import batchActions from '~/frontend/src/actions/batchActions';
import updateItems, {ItemUpdate} from '~/frontend/src/actions/data/updateItems';
import updateReferences, {ReferenceUpdate} from '~/frontend/src/actions/data/updateReferences';
import {UpdateItemBulkMeta} from '~/frontend/src/actions/data/updateItem';
import {UpdateReferenceBulkMeta} from '~/frontend/src/actions/data/updateReference';


type BulkMeta = UpdateItemBulkMeta | UpdateReferenceBulkMeta;

export type ScopedDispatch = ( action : any ) => any;

/**
 * Runs `worker` with a scoped dispatch that aggregates updateItem /
 * updateReference thunks per dataContext. After the worker finishes the
 * collected work is replayed against updateItems / updateReferences and the
 * resulting plain actions are flushed as a single batchActions dispatch so
 * Redux subscribers are notified exactly once.
 */
const dispatchBatched = ( worker : ( dispatch : ScopedDispatch ) => void ) : Thunk<void> =>
    ( realDispatch : AppDispatch, getState : () => StoreState ) => {
        const otherActions         : AnyAction[]                            = [],
              itemUpdatesByContext : { [ ctx : string ] : ItemUpdate[] }      = {},
              refUpdatesByContext  : { [ ctx : string ] : ReferenceUpdate[] } = {};

        const scopedDispatch : ScopedDispatch = ( action : any ) => {
            if ( typeof action === 'function' ) {
                const meta : BulkMeta | undefined = action.__bulk;

                if ( meta?.type === 'updateItem' ) {
                    ( itemUpdatesByContext[ meta.dataContext ] ??= [] ).push( meta.payload );
                    return;
                }
                if ( meta?.type === 'updateReference' ) {
                    ( refUpdatesByContext[ meta.dataContext ] ??= [] ).push( meta.payload );
                    return;
                }

                // unknown thunk: execute with scopedDispatch so nested
                // updateItem / updateReference calls are still aggregated
                return action( scopedDispatch, getState );
            }

            // plain action: collect for the final batch
            otherActions.push( action );
            return action;
        };

        // 1. let the worker collect work
        worker( scopedDispatch );

        // 2. replay aggregated bulk work against a collector. Bulk thunks
        //    perform their side effects (item.clone / reference.clone) here
        //    and return their plain actions which we append to the batch.
        const batched : AnyAction[] = [ ...otherActions ];

        const collectInto = ( target : AnyAction[] ) : AppDispatch => {
            const collector = ( ( a : any ) => {
                if ( typeof a === 'function' ) {
                    return a( collector, getState );
                }
                target.push( a );
                return a;
            } ) as unknown as AppDispatch;

            return collector;
        };

        for ( const ctx of Object.keys( itemUpdatesByContext ) as string[] ) {
            updateItems( ctx, itemUpdatesByContext[ ctx ] )( collectInto( batched ), getState, undefined );
        }
        for ( const ctx of Object.keys( refUpdatesByContext ) as string[] ) {
            updateReferences( ctx, refUpdatesByContext[ ctx ] )( collectInto( batched ), getState, undefined );
        }

        // 3. flush everything in a single dispatch -> subscribers notified once
        if ( batched.length ) {
            realDispatch( batchActions( ...batched ) );
        }
    };

export type DispatchBatched = typeof dispatchBatched;
export default dispatchBatched;